import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Avatar, Popover } from '../ui';

interface MentionUser {
  id: string;
  name: string;
  avatar: string | null;
}

interface MentionDropdownProps {
  channelId: string;
  query: string; // text after @
  onSelect: (user: MentionUser) => void;
  onClose: () => void;
  visible: boolean;
  anchorEl: HTMLElement | null;
}

export default function MentionDropdown({
  channelId,
  query,
  onSelect,
  onClose,
  visible,
  anchorEl,
}: MentionDropdownProps) {
  const [members, setMembers] = useState<MentionUser[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<MentionUser[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Fetch channel members
  useEffect(() => {
    if (!channelId || !visible) return;

    const fetchMembers = async () => {
      try {
        const { data: memberData, error } = await supabase
          .from('chat_channel_members')
          .select('user_id')
          .eq('channel_id', channelId);

        if (error) throw error;

        const userIds = (memberData || []).map(m => m.user_id);
        if (userIds.length === 0) {
          setMembers([]);
          return;
        }

        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, name, avatar')
          .in('id', userIds);

        if (userError) throw userError;

        setMembers(
          (userData || []).map(u => ({
            id: u.id,
            name: u.name || 'Unknown',
            avatar: u.avatar || null,
          }))
        );
      } catch (err) {
        console.error('Error fetching mention members:', err);
      }
    };

    fetchMembers();
  }, [channelId, visible]);

  // Filter members based on query
  useEffect(() => {
    const q = query.toLowerCase();
    const filtered = members.filter(m => m.name.toLowerCase().includes(q));
    setFilteredMembers(filtered);
    setSelectedIndex(0);
  }, [query, members]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredMembers.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredMembers.length - 1
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (filteredMembers[selectedIndex]) {
          onSelect(filteredMembers[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, filteredMembers, selectedIndex, onSelect, onClose]);

  if (!visible || filteredMembers.length === 0) return null;

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      matchWidth
      role="listbox"
      style={{ padding: 0, maxHeight: '192px' }}
    >
      <div className="chat-scrollbar" style={{ padding: '4px' }}>
        {filteredMembers.map((member, idx) => (
          <button
            key={member.id}
            onClick={() => onSelect(member)}
            className="hover-wash pressable w-full flex items-center text-left"
            style={{
              gap: '10px',
              padding: '7px 10px',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              background: idx === selectedIndex ? 'var(--surface-active)' : 'transparent',
              cursor: 'pointer',
            }}
          >
            <Avatar name={member.name} src={member.avatar} size={22} />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
              {member.name}
            </span>
          </button>
        ))}
      </div>
    </Popover>
  );
}
